'use strict';

const libWorkspace = require('pict-section-workspace');

// ContractWorkspace — now a thin subclass of the pict-section-workspace base. The base owns the shell
// (header, tab bar, routing, load, overview FieldGroups, the RecordDates + BidItemMap embeds). This subclass
// keeps ONLY Contract's bespoke Projects tab: the FK-children list (Project.IDContract) plus the association
// management it carries — add an existing project (searchable picker → full-record PUT setting IDContract),
// remove one (IDContract → 0), and create a new project already linked to the contract. Config in
// [[Contract.config.js]]. Mirrors the migration of [[ProjectWorkspace]] onto the shared library.

class ContractWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._projects = [];
		this._bidItems = { Loaded: false, Loading: false, Rows: [] };
	}

	// --- hydrate (runs inside the base load, after the Contract record arrives) ---

	async hydrateAsync(pRecord)
	{
		this._projects = [];
		this._bidItems = { Loaded: false, Loading: false, Rows: [] };
		const tmpRows = await this._getJSON(`Projects/FilteredTo/FBV~IDContract~EQ~${pRecord.IDContract}/0/1000`);
		this._projects = Array.isArray(tmpRows) ? tmpRows : [];
	}

	get idContract() { return this.record.IDContract || this.idRecord; }

	// --- Projects tab (bespoke render) ---

	openProject(pIDProject)
	{
		if (typeof window !== 'undefined' && window.location) { window.location.hash = `#/Project/Workspace/${pIDProject}`; }
	}

	/** The Projects tab body: a management toolbar (gated) + the FK-children table (or an empty state). */
	renderProjects()
	{
		const tmpCanManage = this.canManageProjects();
		const tmpCanCreate = this.canCreateProjects();
		const tmpProjects = this._projects || [];
		const tmpCount = tmpProjects.length;
		const tmpToolbar = (tmpCanManage || tmpCanCreate)
			? `<div class="pw-toolbar">`
				+ `<span class="pw-muted">${tmpCount} ${tmpCount === 1 ? 'project' : 'projects'} under this contract.</span>`
				+ `<span style="flex:1"></span>`
				+ (tmpCanManage ? `<button class="pw-btn is-light" onclick="_Pict.providers.ContractWorkspace.promptAddProject()"><i class="fas fa-link"></i><span>Add existing project</span></button>` : '')
				+ (tmpCanCreate ? `<button class="pw-btn is-link" onclick="_Pict.providers.ContractWorkspace.createProjectUnderContract()"><i class="fas fa-plus"></i><span>New project</span></button>` : '')
				+ `</div>`
			: `<div class="pw-toolbar"><span class="pw-muted">${tmpCount} ${tmpCount === 1 ? 'project' : 'projects'} under this contract.</span></div>`;
		if (!tmpCount) { return `${tmpToolbar}<div class="pw-empty"><i class="fas fa-folder-open"></i><p>No projects are linked to this contract.</p></div>`; }
		const tmpRows = tmpProjects.map((pProject) =>
		{
			const tmpID = Number(pProject.IDProject);
			const tmpName = this._esc(pProject.Name || `Project ${tmpID}`);
			const tmpRemove = tmpCanManage
				? `<button class="pw-btn is-danger" title="Remove from this contract" onclick="event.stopPropagation(); _Pict.providers.ContractWorkspace.confirmRemoveProject(${tmpID}); return false;"><i class="fas fa-link-slash"></i></button>`
				: '';
			return `<tr onclick="_Pict.providers.ContractWorkspace.openProject(${tmpID})" style="cursor:pointer">`
				+ `<td class="pw-cell-strong">${tmpName}</td>`
				+ `<td>${this._esc(pProject.Status || '—')}</td>`
				+ `<td class="pw-muted">${this._esc(pProject.ExternalProjectID || '—')}</td>`
				+ `<td class="pw-row-actions">${tmpRemove}<span class="pw-muted" style="margin-left:.4rem">Open <i class="fas fa-chevron-right"></i></span></td>`
				+ `</tr>`;
		}).join('');
		return `${tmpToolbar}<div class="pw-tablewrap"><table class="pw-table">`
			+ `<thead><tr><th>Project</th><th>Status</th><th>External ID</th><th></th></tr></thead>`
			+ `<tbody>${tmpRows}</tbody></table></div>`;
	}

	// --- Bid Items tab (bespoke render): the contract's bid items, each showing its Project when set ---

	/** OnEnter for the Bid Items tab: lazily fetch the contract's bid items (a contract can carry many), then
	 *  repaint the body. Bid items are CONTRACT-scoped (BidItem.IDContract) and may carry an optional IDProject
	 *  (line-item ↔ bid-item mapping is a project-scoped concern and lives on the Project workspace, not here). */
	async loadBidItemsAsync()
	{
		if (this._bidItems.Loaded || this._bidItems.Loading) { return; }
		this._bidItems.Loading = true;
		this.repaintBody();
		const tmpRows = await this._getJSON(`BidItems/FilteredTo/FBV~IDContract~EQ~${this.idContract}~FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/2000`);
		this._bidItems = { Loaded: true, Loading: false, Rows: Array.isArray(tmpRows) ? tmpRows : [] };
		this.repaintBody();
	}

	/** Resolve a bid item's IDProject to its project name (from the contract's projects loaded in hydrate). */
	_projectName(pIDProject)
	{
		if (!pIDProject) { return ''; }
		const tmpProject = (this._projects || []).find((pP) => Number(pP.IDProject) === Number(pIDProject));
		return tmpProject ? (tmpProject.Name || `Project ${pIDProject}`) : `Project ${pIDProject}`;
	}

	/** The Bid Items tab body: the contract's bid items, with each item's Project shown when it is set. */
	renderBidItems()
	{
		const tmpState = this._bidItems;
		if (tmpState.Loading && !tmpState.Loaded)
		{
			return `<div class="pw-state"><span class="pw-spinner pw-spinner-inline"></span> Loading bid items…</div>`;
		}
		const tmpRows = tmpState.Rows || [];
		const tmpCount = tmpRows.length;
		const tmpToolbar = `<div class="pw-toolbar"><span class="pw-muted">${tmpCount} ${tmpCount === 1 ? 'bid item' : 'bid items'} on this contract.</span></div>`;
		if (!tmpCount)
		{
			return `${tmpToolbar}<div class="pw-empty"><i class="fas fa-gavel"></i><p>No bid items on this contract.</p></div>`;
		}
		const tmpBody = tmpRows.map((pRow) =>
		{
			const tmpProjectName = this._projectName(pRow.IDProject);
			const tmpProjectCell = tmpProjectName
				? `<a href="#/Project/Workspace/${Number(pRow.IDProject)}" onclick="event.stopPropagation()">${this._esc(tmpProjectName)}</a>`
				: `<span class="pw-muted">—</span>`;
			return `<tr>`
				+ `<td class="pw-cell-strong">${this._esc(pRow.Name || `Bid Item ${pRow.IDBidItem}`)}</td>`
				+ `<td>${this._esc(pRow.Description || '—')}</td>`
				+ `<td>${tmpProjectCell}</td>`
				+ `<td>${this._esc(pRow.Units || '—')}</td>`
				+ `<td class="pw-num">${this._formatValue(pRow.Quantity, 'number')}</td>`
				+ `<td class="pw-num">${this._formatValue(pRow.CostPerUnit, 'currency')}</td>`
				+ `</tr>`;
		}).join('');
		return `${tmpToolbar}<div class="pw-tablewrap"><table class="pw-table">`
			+ `<thead><tr><th>Name</th><th>Description</th><th>Project</th><th>Units</th><th class="pw-num">Quantity</th><th class="pw-num">Unit Cost</th></tr></thead>`
			+ `<tbody>${tmpBody}</tbody></table></div>`;
	}

	// --- Project association management (add existing / remove / create under contract) ---

	get modal() { return this.pict.views['Pict-Section-Modal']; }
	_toast(pMessage, pType, pDuration) { if (this.modal && (typeof this.modal.toast === 'function')) { const tmpOptions = { type: pType || 'info' }; if (pDuration !== undefined) { tmpOptions.duration = pDuration; } this.modal.toast(pMessage, tmpOptions); } }

	/** Fail-CLOSED capability check for the mutating actions (no service / unloaded map → false). */
	_canFailClosed(pEntity, pAction)
	{
		const tmpSvc = this.permissions;
		if (!tmpSvc || (typeof tmpSvc.can !== 'function') || !tmpSvc.currentUserReady || !tmpSvc.currentUserReady()) { return false; }
		return !!tmpSvc.can(pEntity, pAction);
	}
	canManageProjects() { return this._canFailClosed('Project', 'Update'); }
	canCreateProjects() { return this._canFailClosed('Project', 'Create'); }

	/** Re-fetch just the projects under this contract + re-render the active (Projects) tab. */
	async _reloadProjectsAsync()
	{
		if (!this.record.IDContract) { return; }
		const tmpRows = await this._getJSON(`Projects/FilteredTo/FBV~IDContract~EQ~${this.record.IDContract}/0/1000`);
		this._projects = Array.isArray(tmpRows) ? tmpRows : this._projects;
		// Repaint the tab bar too (the Projects (N) count is a dynamic label) + the body.
		this.renderWorkspace();
	}

	/** Candidate projects to ADD: those NOT already on this contract, optionally name-filtered. */
	async searchAddableProjectsAsync(pTerm)
	{
		if (!this.record.IDContract) { return []; }
		const tmpTerm = String(pTerm == null ? '' : pTerm).replace(/~/g, '').trim();
		let tmpFilter = `FBV~IDContract~NE~${this.record.IDContract}~FBV~Deleted~EQ~0`;
		if (tmpTerm) { tmpFilter = `FBV~Name~LK~${encodeURIComponent(`%${tmpTerm}%`)}~${tmpFilter}`; }
		const tmpRows = await this._getJSON(`Projects/FilteredTo/${tmpFilter}/0/100`);
		return (Array.isArray(tmpRows) ? tmpRows : [])
			.map((pRow) => ({ IDProject: pRow.IDProject, Name: pRow.Name || `Project ${pRow.IDProject}`, Status: pRow.Status || '', IDContract: pRow.IDContract || 0 }))
			.sort((pA, pB) => (pA.Name.toLowerCase() < pB.Name.toLowerCase() ? -1 : (pA.Name.toLowerCase() > pB.Name.toLowerCase() ? 1 : 0)));
	}

	/** Link an existing project to this contract (full-record PUT setting Project.IDContract). */
	async addProjectToContractAsync(pIDProject)
	{
		if (!this.canManageProjects()) { throw new Error('You don’t have permission to change project assignments.'); }
		const tmpProject = await this._getJSON(`Project/${pIDProject}`);
		if (!tmpProject || !tmpProject.IDProject) { throw new Error('Project not found.'); }
		tmpProject.IDContract = this.record.IDContract;
		const tmpResult = await this._putJSON('Project', tmpProject);
		if (tmpResult && tmpResult.error) { throw new Error('Could not save the project assignment.'); }
		await this._reloadProjectsAsync();
	}

	/** Unlink a project from this contract (sets Project.IDContract back to 0 — the project itself is untouched). */
	async removeProjectFromContractAsync(pIDProject)
	{
		if (!this.canManageProjects()) { throw new Error('You don’t have permission to change project assignments.'); }
		const tmpProject = await this._getJSON(`Project/${pIDProject}`);
		if (!tmpProject || !tmpProject.IDProject) { throw new Error('Project not found.'); }
		tmpProject.IDContract = 0;
		const tmpResult = await this._putJSON('Project', tmpProject);
		if (tmpResult && tmpResult.error) { throw new Error('Could not save the project assignment.'); }
		await this._reloadProjectsAsync();
	}

	confirmRemoveProject(pIDProject)
	{
		const tmpProject = this._projects.find((pP) => Number(pP.IDProject) === Number(pIDProject));
		const tmpName = (tmpProject && tmpProject.Name) || `Project ${pIDProject}`;
		Promise.resolve(this.modal.confirm(`Remove “${tmpName}” from this contract? The project is kept — only its link to this contract is cleared.`, { title: 'Remove from contract', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true }))
			.then((pOk) =>
			{
				if (!pOk) { return; }
				return this.removeProjectFromContractAsync(pIDProject)
					.then(() => this._toast(`Removed “${tmpName}” from this contract.`, 'success'))
					.catch((pError) => this._toast((pError && pError.message) ? pError.message : 'Could not remove the project.', 'error', 6000));
			});
	}

	/** Create a brand-new project already linked to this contract: prefill + open the Create workspace. */
	createProjectUnderContract()
	{
		if (!this.canCreateProjects()) { this._toast('You don’t have permission to create projects.', 'error', 6000); return; }
		const tmpCreate = this.pict.providers.ProjectCreateWorkspace;
		if (tmpCreate && (typeof tmpCreate.prefillNew === 'function')) { tmpCreate.prefillNew({ IDContract: this.record.IDContract }); }
		if (typeof window !== 'undefined' && window.location) { window.location.hash = '#/PSRS/Project/Create'; }
	}

	// --- Add-existing picker (searchable modal) ---

	promptAddProject()
	{
		if (!this.canManageProjects()) { this._toast('You don’t have permission to change project assignments.', 'error', 6000); return; }
		if (!this.modal || (typeof this.modal.show !== 'function')) { return; }
		this._addProjectToken = 0;
		this.modal.show(
			{
				title: 'Add an existing project to this contract',
				content: '<p class="cw-modal-help">Pick a project to link to this contract. Projects already on another contract will be moved to this one.</p>'
					+ '<input id="CW-AddProjSearch" class="input" type="text" autocomplete="off" placeholder="Search projects by name…" oninput="_Pict.providers.ContractWorkspace.onAddProjectSearch(this.value)" />'
					+ '<div id="CW-AddProjResults" class="cw-addproj-list"><div class="cw-addproj-note">Loading projects…</div></div>',
				buttons: [ { Hash: 'close', Label: 'Close' } ],
				width: '520px',
			});
		setTimeout(() => this.onAddProjectSearch(''), 60);
	}

	onAddProjectSearch(pTerm)
	{
		if (this._addProjectTimer) { clearTimeout(this._addProjectTimer); }
		const tmpToken = (this._addProjectToken = (this._addProjectToken || 0) + 1);
		this._addProjectTimer = setTimeout(() =>
		{
			this.searchAddableProjectsAsync(pTerm).then((pRows) =>
			{
				if (tmpToken === this._addProjectToken) { this._renderAddProjectResults(pRows); }
			}).catch(() => { if (tmpToken === this._addProjectToken) { this._renderAddProjectResults(null); } });
		}, 200);
	}

	_renderAddProjectResults(pRows)
	{
		const tmpHost = document.getElementById('CW-AddProjResults');
		if (!tmpHost) { return; }
		if (pRows === null) { tmpHost.innerHTML = '<div class="cw-addproj-note cw-addproj-err">Could not load projects.</div>'; return; }
		if (!pRows.length) { tmpHost.innerHTML = '<div class="cw-addproj-note">No matching projects.</div>'; return; }
		tmpHost.innerHTML = pRows.slice(0, 100).map((pRow) =>
			`<button type="button" class="cw-addproj-row" onclick="_Pict.providers.ContractWorkspace.pickAddProject(${Number(pRow.IDProject)})">`
			+ `<span class="cw-addproj-name">${this._esc(pRow.Name)}</span>`
			+ (pRow.IDContract ? '<span class="cw-addproj-tag">on another contract</span>' : '')
			+ '</button>').join('');
	}

	pickAddProject(pIDProject)
	{
		if (this.modal && (typeof this.modal.dismissModals === 'function')) { this.modal.dismissModals(); }
		this.addProjectToContractAsync(pIDProject)
			.then(() => this._toast('Project added to this contract.', 'success'))
			.catch((pError) => this._toast((pError && pError.message) ? pError.message : 'Could not add the project.', 'error', 6000));
	}
}

module.exports = ContractWorkspaceProvider;
